import { AudioPlayer } from '@components/AudioPlayer';
import { TeamCards } from '@components/TeamCards';
import { OrganizationCards } from '@components/OrganizationCards';
import { DropZone } from '@components/Files/DropZone';
import { UploadProvider } from '@components/Files/UploadProvider';
import { UniversalCard } from '@components/core-ui/UniversalCard';
import { UniversalNoPictureCard } from '@components/core-ui/UniversalNoPictureCard';
import { Tag } from '@components/core-ui/Tag';
import { TagAccent } from '@components/core-ui/Tag/variations/TagAccent';
import { TagDangerRounded } from '@components/core-ui/Tag/variations/TagDangerRounded';
import { TagDefaultRounded } from '@components/core-ui/Tag/variations/TagDefaultRounded';
import { TagFilter } from '@components/core-ui/Tag/variations/TagFilter';
import { TagFilterActive } from '@components/core-ui/Tag/variations/TagFilterActive';
import { TagInfoRounded } from '@components/core-ui/Tag/variations/TagInfoRounded';
import { TagMuted } from '@components/core-ui/Tag/variations/TagMuted';
import { TagMutedRounded } from '@components/core-ui/Tag/variations/TagMutedRounded';
import { TagOutlined } from '@components/core-ui/Tag/variations/TagOutlined';
import { TagSearch } from '@components/core-ui/Tag/variations/TagSearch';
import { TagSearchActive } from '@components/core-ui/Tag/variations/TagSearchActive';
import { TagSuccessRounded } from '@components/core-ui/Tag/variations/TagSuccessRounded';
import { EntityIcon } from '@components/core-ui/EntityIcon';
import { EntityIconCompact } from '@components/core-ui/EntityIcon/variations/EntityIconCompact';
import { EntityIconLg } from '@components/core-ui/EntityIcon/variations/EntityIconLg';
import { EntityIconMd } from '@components/core-ui/EntityIcon/variations/EntityIconMd';
import { EntityIconProfile } from '@components/core-ui/EntityIcon/variations/EntityIconProfile';
import { EntityIconRoundSm } from '@components/core-ui/EntityIcon/variations/EntityIconRoundSm';
import { EntityIconTableMuted30 } from '@components/core-ui/EntityIcon/variations/EntityIconTableMuted30';
import { EntityIconXl } from '@components/core-ui/EntityIcon/variations/EntityIconXl';
import { PagePagination } from '@components/core-ui/Pagination';
import { Skeleton } from '@components/core-ui/Skeleton';
import { AbsoluteSmallSpinner, AbsoluteSpinner, RelativeSpinner65 } from '@components/core-ui/spinners/Spinner';
import { OrganizationTeamResponseDto } from 'app/API';
import { LinearProgressLoader } from '@components/core-ui/spinners/LinearProgressLoader';
import autoscriptLogo from '@wrappers/GuestApp/components/LoginLayout/images/autoscript.png';
import { useState } from 'react';

const labeledItemClassName = 'flex min-w-[140px] flex-col gap-1.5';
const labeledItemTitleClassName = 'text-xs font-medium text-[var(--color-text)]';
const labeledItemHintClassName = 'text-xs text-foreground-muted';

export const TagsDemo = () => (
  <div className="flex flex-wrap gap-4">
    <div className={labeledItemClassName}>
      <span className={labeledItemTitleClassName}>Tag</span>
      <Tag>Default</Tag>
    </div>
    <div className={labeledItemClassName}>
      <span className={labeledItemTitleClassName}>Tag</span>
      <span className={labeledItemHintClassName}>with onDelete</span>
      <Tag onDelete={() => undefined}>Removable</Tag>
    </div>
    <div className={labeledItemClassName}>
      <span className={labeledItemTitleClassName}>TagDefaultRounded</span>
      <TagDefaultRounded>Default</TagDefaultRounded>
    </div>
    <div className={labeledItemClassName}>
      <span className={labeledItemTitleClassName}>TagMuted</span>
      <TagMuted>Muted</TagMuted>
    </div>
    <div className={labeledItemClassName}>
      <span className={labeledItemTitleClassName}>TagAccent</span>
      <TagAccent>Accent</TagAccent>
    </div>
    <div className={labeledItemClassName}>
      <span className={labeledItemTitleClassName}>TagOutlined</span>
      <TagOutlined>Outlined</TagOutlined>
    </div>
    <div className={labeledItemClassName}>
      <span className={labeledItemTitleClassName}>TagInfoRounded</span>
      <TagInfoRounded>Info</TagInfoRounded>
    </div>
    <div className={labeledItemClassName}>
      <span className={labeledItemTitleClassName}>TagDangerRounded</span>
      <TagDangerRounded>Danger</TagDangerRounded>
    </div>
    <div className={labeledItemClassName}>
      <span className={labeledItemTitleClassName}>TagSuccessRounded</span>
      <TagSuccessRounded>Success</TagSuccessRounded>
    </div>
    <div className={labeledItemClassName}>
      <span className={labeledItemTitleClassName}>TagMutedRounded</span>
      <TagMutedRounded>Muted</TagMutedRounded>
    </div>
    <div className={labeledItemClassName}>
      <span className={labeledItemTitleClassName}>TagFilter</span>
      <TagFilter>Filter</TagFilter>
    </div>
    <div className={labeledItemClassName}>
      <span className={labeledItemTitleClassName}>TagFilterActive</span>
      <TagFilterActive>Active</TagFilterActive>
    </div>
    <div className={labeledItemClassName}>
      <span className={labeledItemTitleClassName}>TagSearch</span>
      <TagSearch>Search</TagSearch>
    </div>
    <div className={labeledItemClassName}>
      <span className={labeledItemTitleClassName}>TagSearchActive</span>
      <TagSearchActive>Search active</TagSearchActive>
    </div>
  </div>
);

export const EntityIconDemo = () => (
  <div className="flex flex-wrap gap-4">
    <div className={labeledItemClassName}>
      <span className={labeledItemTitleClassName}>EntityIcon</span>
      <EntityIcon name="Jane Doe" />
    </div>
    <div className={labeledItemClassName}>
      <span className={labeledItemTitleClassName}>EntityIconCompact</span>
      <EntityIconCompact name="Jane Doe" />
    </div>
    <div className={labeledItemClassName}>
      <span className={labeledItemTitleClassName}>EntityIconRoundSm</span>
      <EntityIconRoundSm name="Jane Doe" />
    </div>
    <div className={labeledItemClassName}>
      <span className={labeledItemTitleClassName}>EntityIconMd</span>
      <EntityIconMd name="Jane Doe" />
    </div>
    <div className={labeledItemClassName}>
      <span className={labeledItemTitleClassName}>EntityIconLg</span>
      <EntityIconLg name="Jane Doe" />
    </div>
    <div className={labeledItemClassName}>
      <span className={labeledItemTitleClassName}>EntityIconXl</span>
      <EntityIconXl name="Jane Doe" />
    </div>
    <div className={labeledItemClassName}>
      <span className={labeledItemTitleClassName}>EntityIconProfile</span>
      <EntityIconProfile name="Jane Doe" />
    </div>
    <div className={labeledItemClassName}>
      <span className={labeledItemTitleClassName}>EntityIconTableMuted30</span>
      <EntityIconTableMuted30 name="Jane Doe" />
    </div>
    <div className={labeledItemClassName}>
      <span className={labeledItemTitleClassName}>EntityIconLg</span>
      <span className={labeledItemHintClassName}>with logo</span>
      <EntityIconLg name="AutoScript" src={autoscriptLogo} />
    </div>
  </div>
);

export const SkeletonDemo = () => (
  <div className="flex w-full max-w-xs flex-col gap-3 rounded-[10px] bg-[var(--color-surface)] p-5">
    <Skeleton className="h-[18px] w-[120px]" />
    <Skeleton className="h-[34px] w-24 rounded-lg" />
    <Skeleton className="h-3 w-full" />
  </div>
);

export const SpinnersDemo = () => (
  <div className="flex flex-col gap-6">
    <div className="flex flex-wrap gap-4">
      <div className={labeledItemClassName}>
        <span className={labeledItemTitleClassName}>RelativeSpinner65</span>
        <RelativeSpinner65 />
      </div>
      <div className={labeledItemClassName}>
        <span className={labeledItemTitleClassName}>AbsoluteSpinner</span>
        <div className="relative h-24 w-full max-w-xs rounded border border-[var(--color-border)]">
          <AbsoluteSpinner />
        </div>
      </div>
      <div className={labeledItemClassName}>
        <span className={labeledItemTitleClassName}>AbsoluteSmallSpinner</span>
        <div className="relative h-24 w-full max-w-xs rounded border border-[var(--color-border)]">
          <AbsoluteSmallSpinner />
        </div>
      </div>
    </div>
    <div className="w-full max-w-md min-w-0">
      <p className="mb-2 text-xs font-medium text-[var(--color-text)]">LinearProgressLoader</p>
      <LinearProgressLoader active />
    </div>
  </div>
);

export const PaginationDemo = () => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  return (
    <PagePagination
      embedded
      totalItemsCount={42}
      page={page}
      onPageChange={setPage}
      pageSize={pageSize}
      setPageSize={setPageSize}
    />
  );
};

export const CardsDemo = () => (
  <div className="flex flex-wrap gap-4">
    <div className="w-[280px]">
      <UniversalCard
        name="Sample card"
        content={<span className="text-sm">Card content</span>}
        actions={[{ label: 'Edit', onClick: () => undefined }]}
      />
    </div>
    <div className="w-[280px]">
      <UniversalNoPictureCard
        content={<span className="text-sm">No picture card</span>}
        actions={[{ label: 'Open', onClick: () => undefined }]}
      />
    </div>
  </div>
);

const mockTeams: OrganizationTeamResponseDto[] = [
  {
    id: '1',
    name: 'Team Alpha',
    slug: 'alpha',
    members: 3,
    owner: false,
    colour: '#40608f',
    default: false,
    createdAt: '2024-01-01T00:00:00.000Z',
    updatedAt: {},
  },
  {
    id: '2',
    name: 'Team Beta',
    slug: 'beta',
    members: 5,
    owner: true,
    colour: '#7cb1fe',
    default: true,
    createdAt: '2024-01-01T00:00:00.000Z',
    updatedAt: {},
  },
];

export const TeamCardsDemo = () => <TeamCards teams={mockTeams} handleRemoval={() => undefined} />;

export const OrganizationCardsDemo = () => <OrganizationCards />;

export const FilesDemo = () => (
  <UploadProvider config={{ mode: 'deferred' }}>
    <div className="max-w-md">
      <DropZone onFiles={() => undefined} />
    </div>
  </UploadProvider>
);

export const AudioPlayerDemo = () => (
  <div className="w-full min-w-0">
    <AudioPlayer src="https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3" />
  </div>
);
